import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Chip,
  Box,
  Paper,
  Divider
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import JSONView from '@uiw/react-json-view';
import { Workflow } from '../../types/workflow';

interface WorkflowViewModalProps {
  open: boolean;
  onClose: () => void;
  workflow: Workflow | null;
}

const WorkflowViewModal: React.FC<WorkflowViewModalProps> = ({ 
  open, 
  onClose, 
  workflow
}) => {
  if (!workflow) return null;

  const getWorkflowSummary = (workflow: Workflow) => {
    if (!workflow || !workflow.nodes) {
      return {
        nodeCount: 0,
        modelTypes: [],
        stepCount: 0,
        inputCount: 0
      };
    }

    const nodeCount = workflow.nodes.length;
    const modelTypes = [...new Set(workflow.nodes
      .filter(node => node && node.procedural_memory_args)
      .map(node => node.procedural_memory_args.model)
      .filter(Boolean)
    )];
    const stepCount = workflow.nodes.reduce((acc, node) => 
      acc + (node?.procedural_memory_args?.steps?.length || 0), 0
    );

    return {
      nodeCount,
      modelTypes,
      stepCount,
      inputCount: Object.keys(workflow.input || {}).length
    };
  };

  const summary = getWorkflowSummary(workflow);

  const renderStepDetails = (step: any) => {
    const getStepIcon = (type: string) => {
      switch (type) {
        case 'inference_chain':
          return '🧠';
        case 'code':
          return '📝';
        case 'api':
          return '🔌';
        default:
          return '📋';
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <span>{getStepIcon(step.type)}</span>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.params.description}</Typography>
          <Chip label={step.type} color="primary" size="small" />
        </Box>
        
        {step.params.definition.condition && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">Condition:</Typography>
            <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mt: 0.5, fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {step.params.definition.condition}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">Definition:</Typography>
          <Box sx={{ mt: 0.5 }}>
            <JSONView 
              value={step.params.definition}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Visibility />
          <Typography variant="h6">Workflow Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">ID:</Typography>
                <Typography variant="body2">{workflow.id}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Status:</Typography>
                <Chip 
                  label={workflow.is_enabled ? 'Enabled' : 'Disabled'} 
                  color={workflow.is_enabled ? 'success' : 'error'} 
                  size="small" 
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Nodes:</Typography>
                <Typography variant="body2">{summary.nodeCount}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="textSecondary">Total Steps:</Typography>
                <Typography variant="body2">{summary.stepCount}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="textSecondary">Models:</Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {summary.modelTypes.map((model, idx) => (
                    <Chip key={idx} label={model || 'No model'} color="primary" size="small" />
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Input Parameters</Typography>
            <JSONView 
              value={workflow.input || {}}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
            />
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Nodes</Typography>
            {workflow.nodes.map((node, index) => (
              <Accordion key={node._id}>
                <AccordionSummary>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography>Node {index + 1}</Typography>
                    <Chip label={node.type} color="primary" size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="textSecondary">ID:</Typography>
                          <Typography variant="body2">{node._id}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="textSecondary">Type:</Typography>
                          <Typography variant="body2">{node.type}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="caption" color="textSecondary">Dependencies:</Typography>
                          <Box>
                            {node.depends_on?.length > 0 ? (
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {node.depends_on.map((dep, idx) => (
                                  <Chip key={idx} label={dep} size="small" />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2">None</Typography>
                            )}
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="textSecondary">Model:</Typography>
                          <Typography variant="body2">{node.procedural_memory_args?.model || 'No model'}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="textSecondary">Temperature:</Typography>
                          <Typography variant="body2">{node.procedural_memory_args?.model_temp || 0}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {node.procedural_memory_args?.steps?.length > 0 && (
                      <>
                        <Divider>Steps ({node.procedural_memory_args.steps.length})</Divider>
                        {node.procedural_memory_args.steps.map((step: any, stepIndex: number) => (
                          <Accordion key={step.params.id}>
                            <AccordionSummary>
                              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                <Typography>Step {stepIndex + 1}</Typography>
                                <Chip label={step.type} color="primary" size="small" />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {renderStepDetails(step)}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowViewModal;